import {
    type ReactNode,
    Fragment
} from "react";
import {
    AccessibilityInfo,
    Platform,
    View,
    Text
} from "react-native";
import {
    NCoreUIKitTheme
} from "../core/hooks";
import {
    useSafeAreaInsets
} from "react-native-safe-area-context";
import type {
    ToastStackMode,
    ToastAlign,
    ToastSide
} from "../components/toast/type";
import {
    type ToastPromiseOptionsType,
    type ToastPromiseStateType,
    type ToastContextType,
    type ToastDataType
} from "../types/toast";
import NCoreContext, {
    type ConfigType
} from "ncore-context";
import {
    closeRegisteredToast,
    forgetClosingToast,
    isToastClosing
} from "../components/toast/registry";
import {
    Portal
} from "../helpers/portalize";
import {
    uuid
} from "../utils";
import toastStylesheet, {
    getToastStackAnchorStyle,
    getToastAnchorStyle,
    TOAST_STACK_MODES,
    TOAST_ALIGNS,
    TOAST_SIDES
} from "../components/toast/stylesheet";
import noticeStylesheet, {
    getNoticeLiveRegionStyle,
    getNoticeAnnouncement
} from "../components/notice/stylesheet";
import ToastStack from "../components/toast/internal/stack";
import Toast from "../components/toast";

class NCoreUIKitToast extends NCoreContext<ToastContextType, ConfigType<ToastContextType>> {
    isAlwaysExpanded: boolean;

    constructor({
        isAlwaysExpanded = false,
        data = []
    }: {
        isAlwaysExpanded?: boolean;
        data?: Array<ToastDataType>
    }) {
        super({
            promise: () => "",
            close: () => {},
            open: () => "",
            data: data
        }, {
            key: "NCoreUIKit-ToastContext"
        });

        this.isAlwaysExpanded = isAlwaysExpanded;
    };

    open = (toastData: ToastDataType) => {
        const currentData = this.state.data;

        const toastID = toastData.id ? toastData.id : uuid();

        currentData.push({
            ...toastData,
            id: toastID
        });

        this.setState({
            data: currentData
        });

        this.announce(toastData);

        return toastID;
    };

    private announce = (toastData: ToastDataType) => {
        if(Platform.OS === "ios") {
            AccessibilityInfo.announceForAccessibility(toastData.subTitle ? `${toastData.title}, ${toastData.subTitle}` : toastData.title);
        }
    };

    private replace = (id: string, toastData: ToastDataType) => {
        const currentData = this.state.data;

        const keyIndex = currentData.findIndex((toast) => toast.id === id);

        if(keyIndex === -1 || isToastClosing(id)) {
            return;
        }

        currentData[keyIndex] = {
            ...toastData,
            id
        };

        this.setState({
            data: currentData
        });

        this.announce(toastData);
    };

    promise = <T, >(promise: Promise<T>, options: ToastPromiseOptionsType<T>) => {
        const {
            loading,
            success,
            error,
            ...sharedProps
        } = options;

        const getState = (state: ToastPromiseStateType): ToastDataType => typeof state === "string" ? {
            title: state
        } : state;

        const toastID = this.open({
            ...sharedProps,
            ...getState(loading),
            isLoading: true
        });

        promise.then((data: T) => {
            this.replace(toastID, {
                type: "success",
                ...sharedProps,
                ...getState(typeof success === "function" ? success(data) : success),
                isLoading: false
            });
        }, (reason: unknown) => {
            this.replace(toastID, {
                type: "danger",
                ...sharedProps,
                ...getState(typeof error === "function" ? error(reason) : error),
                isLoading: false
            });
        });

        return toastID;
    };

    close = (props?: {
        index?: number;
        id?: string;
    }) => {
        const currentData = this.state.data;

        if (props && props.id) {
            const keyIndex = currentData.findIndex((toast) => toast.id === props.id);

            if (keyIndex !== -1) {
                if(closeRegisteredToast(props.id)) {
                    return;
                }

                currentData.splice(keyIndex, 1);

                forgetClosingToast(props.id);

                this.setState({
                    data: currentData
                });
            }

            return;
        }

        if (props && props.index !== undefined) {
            if(closeRegisteredToast(currentData[props.index]?.id)) {
                return;
            }

            forgetClosingToast(currentData[props.index]?.id);

            currentData.splice(props.index, 1);

            this.setState({
                data: currentData
            });

            return;
        }

        if(closeRegisteredToast(currentData[currentData.length - 1]?.id)) {
            return;
        }

        forgetClosingToast(currentData[currentData.length - 1]?.id);

        currentData.pop();

        this.setState({
            data: currentData
        });
    };

    Render = ({
        children
    }: {
        children: ReactNode;
    }) => {
        const {
            data
        } = this.useContext();

        const {
            borders,
            spaces
        } = NCoreUIKitTheme.useContext();

        const insets = useSafeAreaInsets();

        const renderLiveRegion = (isAssertive: boolean) => {
            const announcedData = data.filter((item: ToastDataType) => (item.type === "danger") === isAssertive && !!item.title);

            return <View
                aria-live={isAssertive ? "assertive" : "polite"}
                style={[
                    noticeStylesheet.liveRegion,
                    getNoticeLiveRegionStyle({
                        borders
                    })
                ]}
                role="log"
            >
                {announcedData.map((item: ToastDataType) => {
                    return <Text
                        key={`NCoreUIKit-ToastAnnouncement-${item.id}-${item.title}`}
                    >
                        {getNoticeAnnouncement({
                            body: item.subTitle,
                            title: item.title
                        })}
                    </Text>;
                })}
            </View>;
        };

        const renderLiveRegions = () => {
            if(Platform.OS !== "web") {
                return null;
            }

            return <Fragment>
                {renderLiveRegion(false)}
                {renderLiveRegion(true)}
            </Fragment>;
        };

        const renderToastItem = (item: ToastDataType) => {
            return <Toast
                key={`NCoreUIKit-Toast-${item.id}`}
                {...item}
                onClosed={(props) => {
                    this.close({
                        id: item.id
                    });

                    if(item.onClosed) item.onClosed(props);
                }}
                isLiveRegionEnabled={Platform.OS !== "web"}
                isWorkWithPortal={false}
                id={item.id as string}
            />;
        };

        const renderListAnchor = (side: ToastSide, align: ToastAlign, anchorData: Array<ToastDataType>) => {
            const edgeOffset = Math.max(0, ...anchorData.map((item: ToastDataType) => item.edgeOffset ?? 0));

            const orderedData = side === "top" ? anchorData.slice().reverse() : anchorData;

            return <Portal
                key={`NCoreUIKit-ToastAnchor-${side}-${align}-list`}
                name="toast-system"
            >
                <View
                    style={[
                        toastStylesheet.anchor,
                        getToastAnchorStyle({
                            stackMode: "list",
                            edgeOffset,
                            insets,
                            spaces,
                            align,
                            side
                        })
                    ]}
                >
                    {orderedData.map(renderToastItem)}
                </View>
            </Portal>;
        };

        const renderAnchor = (side: ToastSide, align: ToastAlign, stackMode: ToastStackMode) => {
            const anchorData = data.filter((item: ToastDataType) => (item.side ?? "bottom") === side && (item.align ?? "center") === align && (item.stackMode ?? "deck") === stackMode);

            if(!anchorData.length) {
                return null;
            }

            if(stackMode === "list") {
                return renderListAnchor(side, align, anchorData);
            }

            const edgeOffset = Math.max(0, ...anchorData.map((item: ToastDataType) => item.edgeOffset ?? 0));

            const orderedData = anchorData.slice().reverse();

            return <Portal
                key={`NCoreUIKit-ToastAnchor-${side}-${align}-deck`}
                name="toast-system"
            >
                <ToastStack
                    ids={orderedData.map((item: ToastDataType) => item.id as string)}
                    isAlwaysExpanded={this.isAlwaysExpanded}
                    anchorStyle={getToastStackAnchorStyle({
                        edgeOffset,
                        insets,
                        spaces,
                        align,
                        side
                    })}
                    align={align}
                    side={side}
                >
                    {orderedData.map(renderToastItem)}
                </ToastStack>
            </Portal>;
        };

        return <Fragment>
            {children}
            {renderLiveRegions()}
            {TOAST_SIDES.flatMap((side) => TOAST_ALIGNS.flatMap((align) => TOAST_STACK_MODES.map((stackMode) => renderAnchor(side, align, stackMode))))}
        </Fragment>;
    };
}
export default NCoreUIKitToast;
